import { useOrganizationStore } from '~/store/organization';
import { useOrganizationDomain } from '~/composables/use-organization-domain';
import { SearchResultCardDto, SearchSourceType } from '~/api/search';

export type SearchReturnContext = {
  query: string;
  totalCount: number;
  type?: string;
  page?: number;
};

export const SEARCH_RETURN_QUERY_PARAM = 'from_search';
export const SEARCH_RETURN_COUNT_PARAM = 'from_search_count';
export const SEARCH_RETURN_TYPE_PARAM = 'from_search_type';
export const SEARCH_RETURN_PAGE_PARAM = 'from_search_page';

export const useSearchResultUrl = () => {
  const organizationDomain = useOrganizationDomain();
  const { organization } = storeToRefs(useOrganizationStore());

  const createHubUrl = (hubSlug: string): string => {
    if (organizationDomain !== null || !organization.value) {
      return `/${hubSlug}`;
    }
    return `/org/${organization.value.slug}/${hubSlug}`;
  };

  const createModuleUrl = (
    moduleSlug: string,
    curriculumId: number | null | undefined,
  ): string =>
    curriculumId
      ? `/learning/${curriculumId}/modules/${moduleSlug}`
      : `/learning/modules/${moduleSlug}`;

  const resolveCardUrl = (card: SearchResultCardDto): string | null => {
    switch (card.sourceType) {
      case SearchSourceType.CONTENT: {
        if (!card.parentHub || !card.slug) return null;
        return `/content/all-content/${card.parentHub.hubSlug}/${card.slug}`;
      }
      case SearchSourceType.PRODUCT: {
        return `/store?product_id=${card.id}`;
      }
      case SearchSourceType.MODULE: {
        if (!card.slug) return null;
        return createModuleUrl(card.slug, card.curriculumId);
      }
      case SearchSourceType.ACTIVITY: {
        if (!card.parentModule) return null;
        return createModuleUrl(card.parentModule.slug, card.curriculumId);
      }
      case SearchSourceType.CURRICULUM: {
        return `/learning/${card.id}`;
      }
      default: {
        return null;
      }
    }
  };

  const withSearchReturn = (
    url: string,
    context: SearchReturnContext,
  ): string => {
    const [path, queryString] = url.split('?');
    const params = new URLSearchParams(queryString ?? '');
    params.set(SEARCH_RETURN_QUERY_PARAM, context.query);
    params.set(SEARCH_RETURN_COUNT_PARAM, String(context.totalCount));
    if (context.type) params.set(SEARCH_RETURN_TYPE_PARAM, context.type);
    if (context.page && context.page > 1) {
      params.set(SEARCH_RETURN_PAGE_PARAM, String(context.page));
    }
    return `${path}?${params.toString()}`;
  };

  return { createHubUrl, createModuleUrl, resolveCardUrl, withSearchReturn };
};
